import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { fmt, fmtHoursDays, colorFor } from "../helpers/format"

const t = (key, opts) => I18n.t(`projects.hours.${key}`, opts)

const Kpi = ({ label, value, amount, hint, tone = "text-slate-900" }) => (
  <div className="px-4 py-3 bg-white border border-slate-200 rounded-xl">
    <div className="text-[10.5px] uppercase tracking-wider font-medium text-slate-500">{label}</div>
    <div className={`text-[20px] font-semibold tabular-nums leading-tight mt-0.5 ${tone}`}>{value}</div>
    {amount && <div className="text-[13px] font-medium tabular-nums text-slate-600 mt-0.5">{fmt(amount)}</div>}
    {hint && <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>}
  </div>
)

const hoursShort = hours => `${String(Math.round((Number(hours) || 0) * 10) / 10).replace(".", ",")} h`

const HoursCell = ({ hours, amount, tone = "text-slate-900", bold }) => (
  <div className="text-right tabular-nums">
    <div className={`${bold ? "font-semibold" : "font-medium"} ${tone}`}>{hoursShort(hours)}</div>
    {amount && <div className="text-[11px] text-slate-400">{fmt(amount)}</div>}
  </div>
)

const Gauge = ({ sold, planned, done }) => {
  const scale   = Math.max(sold, planned, done, 1)
  const width   = value => `${Math.min(100, (value / scale) * 100)}%`
  const overrun = sold > 0 && done > sold
  return (
    <div className="relative h-2 rounded-full bg-slate-100 overflow-hidden">
      <div className="absolute inset-y-0 left-0 bg-sky-200" style={{ width: width(planned) }} />
      <div className={`absolute inset-y-0 left-0 ${overrun ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: width(done) }} />
      {sold > 0 && <div className="absolute inset-y-[-2px] w-[2px] bg-slate-700" style={{ left: `calc(${width(sold)} - 1px)` }} />}
    </div>
  )
}

const RateBand = ({ rate }) => {
  if (!rate) return null
  const set = !!rate.amount
  return (
    <div className={`flex flex-wrap items-center gap-x-6 gap-y-2 px-5 py-3 rounded-xl border mb-4 ${set ? "bg-white border-slate-200" : "bg-amber-50 border-amber-200"}`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className={`h-9 w-9 shrink-0 rounded-full grid place-items-center ${set ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-700"}`}>
          <FontAwesomeIcon icon="coins" />
        </span>
        <div className="min-w-0">
          <div className="text-[10.5px] uppercase tracking-wider font-medium text-slate-500">{t("rate.title")}</div>
          {set ? (
            <div className="flex items-baseline gap-2">
              <span className="text-[17px] font-semibold tabular-nums text-slate-900">{t("rate.per_hour", { rate: fmt(rate.amount) })}</span>
              <span className="text-[11.5px] text-slate-500">{t(`rate.${rate.source}`)}</span>
            </div>
          ) : (
            <div className="text-[13px] font-medium text-amber-800">{t("rate.none")}</div>
          )}
        </div>
      </div>
      <p className="flex-1 min-w-[240px] m-0 text-[12px] text-slate-500 leading-snug">{t("rate.rule")}</p>
      {rate.edit_path && (
        <a href={rate.edit_path} className={`shrink-0 px-3 py-1.5 rounded-lg text-[12.5px] border ${set ? "text-slate-600 border-slate-200 bg-white hover:bg-slate-50" : "text-white bg-amber-600 border-amber-600 hover:bg-amber-700"}`}>
          <FontAwesomeIcon icon="pen" className="mr-1.5" />{t("rate.edit")}
        </a>
      )}
    </div>
  )
}

const UpcomingStrip = ({ days }) => (
  <div className="grid grid-cols-[repeat(14,minmax(0,1fr))] gap-1">
    {days.map(day => {
      const date  = new Date(`${day.date}T00:00:00`)
      const count = day.people.length
      return (
        <div
          key={day.date}
          title={day.people.join(", ")}
          className={`rounded-lg border px-1.5 py-2 text-center ${day.weekend ? "bg-slate-50 border-slate-100" : "bg-white border-slate-200"}`}
        >
          <div className="text-[10px] uppercase tracking-wider text-slate-400">
            {date.toLocaleDateString(I18n.locale, { weekday: "short" }).replace(".", "")}
          </div>
          <div className="text-[12px] font-medium text-slate-700 tabular-nums">{date.getDate()}</div>
          <div className={`mt-1 mx-auto h-6 w-6 rounded-full grid place-items-center text-[11px] font-semibold tabular-nums ${count ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-300"}`}>
            {count}
          </div>
        </div>
      )
    })}
  </div>
)

const workerRate = worker => {
  if (!worker.rate) return null
  const source = worker.rate_source === "user" ? t("rate.user") : worker.rate_source ? t(`rate.${worker.rate_source}`) : null
  return [t("rate.per_hour", { rate: fmt(worker.rate) }), source].filter(Boolean).join(" · ")
}

const LOT_GRID = "grid grid-cols-[minmax(0,1.6fr)_96px_96px_96px_minmax(0,1fr)] gap-3 px-5"

const HoursSection = ({ hours }) => {
  if (!hours) return null

  const { totals = {}, lots = [], workers = [], upcoming = [], rate, hours_per_day: perDay } = hours
  const empty   = lots.length === 0 && workers.length === 0
  const overrun = totals.sold > 0 && totals.done > totals.sold

  return (
    <section id="section-hours" className="px-7 scroll-mt-24">
      <header className="pl-[16px] mb-3 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">{t("title")}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">{t("subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={hours.hours_path} className="px-3 py-1.5 rounded-lg text-[12.5px] text-slate-600 border border-slate-200 bg-white hover:bg-slate-50">
            <FontAwesomeIcon icon="stopwatch" className="mr-1.5" />{t("open_hours")}
          </a>
          {hours.can_plan && (
            <a href={hours.planning_path} className="px-3 py-1.5 rounded-lg text-[12.5px] text-white bg-brand-500 hover:bg-brand-600">
              <FontAwesomeIcon icon="calendar-days" className="mr-1.5" />{t("open_planning")}
            </a>
          )}
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Kpi label={t("kpi.sold")}      value={fmtHoursDays(totals.sold, perDay)}      amount={totals.sold_amount} />
        <Kpi label={t("kpi.planned")}   value={fmtHoursDays(totals.planned, perDay)}   amount={totals.planned_amount} tone="text-sky-700"
             hint={totals.to_plan > 0 ? t("kpi.to_plan", { hours: hoursShort(totals.to_plan) }) : null} />
        <Kpi label={t("kpi.done")}      value={fmtHoursDays(totals.done, perDay)}      amount={totals.done_amount}
             tone={overrun ? "text-rose-700" : "text-emerald-700"} />
        <Kpi label={t("kpi.approved")}  value={fmtHoursDays(totals.approved, perDay)}  amount={totals.approved_amount} />
        <Kpi label={t("kpi.remaining")} value={fmtHoursDays(totals.remaining, perDay)} amount={totals.remaining_amount} />
      </div>

      <RateBand rate={rate} />

      {empty ? (
        <div className="bg-white border border-slate-200 rounded-xl px-6 py-12 text-center">
          <FontAwesomeIcon icon="helmet-safety" className="text-slate-300 text-2xl mb-3" />
          <div className="text-[13px] text-slate-600 font-medium">{t("empty.title")}</div>
          <div className="text-[12.5px] text-slate-400 mt-1">{t("empty.hint")}</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-4">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className={`${LOT_GRID} py-2.5 bg-slate-50 border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500 font-medium`}>
              <div>{t("columns.lot")}</div>
              <div className="text-right">{t("columns.sold")}</div>
              <div className="text-right">{t("columns.planned")}</div>
              <div className="text-right">{t("columns.done")}</div>
              <div className="flex items-center gap-3 normal-case tracking-normal text-[10.5px]">
                <span className="flex items-center gap-1"><i className="inline-block h-2 w-2 rounded-full bg-sky-200" />{t("legend.planned")}</span>
                <span className="flex items-center gap-1"><i className="inline-block h-2 w-2 rounded-full bg-emerald-500" />{t("legend.done")}</span>
                <span className="flex items-center gap-1"><i className="inline-block h-2.5 w-[2px] bg-slate-700" />{t("legend.sold")}</span>
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {lots.map(lot => (
                <div key={lot.id ?? "none"} className={`${LOT_GRID} py-3 items-center text-[13px]`}>
                  <div className="min-w-0 truncate text-slate-900 font-medium" title={lot.title}>
                    {lot.title || <span className="text-slate-400 font-normal">{t("no_lot")}</span>}
                  </div>
                  <HoursCell hours={lot.sold}    amount={lot.sold_amount}    tone="text-slate-600" />
                  <HoursCell hours={lot.planned} amount={lot.planned_amount} tone="text-sky-700" />
                  <HoursCell hours={lot.done}    amount={lot.done_amount}    tone={lot.sold > 0 && lot.done > lot.sold ? "text-rose-700" : "text-slate-900"} bold />
                  <Gauge sold={lot.sold} planned={lot.planned} done={lot.done} />
                </div>
              ))}
            </div>
            {lots.length > 1 && (
              <div className={`${LOT_GRID} py-3 items-center text-[13px] bg-slate-50 border-t border-slate-200`}>
                <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-600">{t("kpi.sold")} / {t("kpi.planned")} / {t("kpi.done")}</div>
                <HoursCell hours={totals.sold}    amount={totals.sold_amount}    tone="text-slate-700" bold />
                <HoursCell hours={totals.planned} amount={totals.planned_amount} tone="text-sky-700" bold />
                <HoursCell hours={totals.done}    amount={totals.done_amount}    tone={overrun ? "text-rose-700" : "text-slate-900"} bold />
                <Gauge sold={totals.sold} planned={totals.planned} done={totals.done} />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-[10.5px] uppercase tracking-wider font-medium text-slate-500 mb-3">{t("upcoming")}</div>
              <UpcomingStrip days={upcoming} />
            </div>

            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500 font-medium">
                {t("team", { count: workers.length })}
              </div>
              <div className="divide-y divide-slate-100">
                {workers.map(worker => (
                  <div key={worker.id} className="flex items-center gap-3 px-4 py-2.5 text-[13px]">
                    <span className="h-7 w-7 shrink-0 rounded-full grid place-items-center text-[10.5px] font-semibold text-white"
                          style={{ background: worker.color || colorFor(worker.name) }}>
                      {worker.name.split(/\s+/).slice(0, 2).map(part => part[0]).join("").toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-slate-900 font-medium">{worker.name}</div>
                      <div className="text-[11px] text-slate-400 truncate">
                        {workerRate(worker) || (worker.last_day ? t("last_day", { date: new Date(`${worker.last_day}T00:00:00`).toLocaleDateString(I18n.locale, { day: "numeric", month: "short" }) }) : t("not_started"))}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="tabular-nums font-semibold text-slate-900">{hoursShort(worker.done)}</div>
                      {worker.done_amount
                        ? <div className="text-[11px] tabular-nums text-slate-500">{fmt(worker.done_amount)}</div>
                        : <div className="text-[11px] tabular-nums text-sky-700">{t("planned_short", { hours: hoursShort(worker.planned) })}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export default HoursSection
